"use client";

import { useState } from "react";

import { Button } from "@heroui/button";
import { CheckCircle2, X } from "lucide-react";

import { ClaimConfirmationDialog } from "./ClaimConfirmationDialog";

interface ClaimActionsProps {
  claimToken: string;
  status: string;
  onMarkCompleted?: (claimToken: string) => Promise<void>;
  onUnclaim?: (claimToken: string) => Promise<void>;
  size?: "sm" | "md";
  variant?: "flat" | "solid";
  isLoading?: boolean;
}

interface ClaimDialogsProps {
  claimToken: string;
  isLoading: boolean;
  showCompleteDialog: boolean;
  showUnclaimDialog: boolean;
  onCompleteConfirm: () => void;
  onUnclaimConfirm: () => void;
  onSetCompleteDialog: (open: boolean) => void;
  onSetUnclaimDialog: (open: boolean) => void;
}

function ClaimDialogs({
  claimToken: _claimToken,
  isLoading,
  onCompleteConfirm,
  onSetCompleteDialog,
  onSetUnclaimDialog,
  onUnclaimConfirm,
  showCompleteDialog,
  showUnclaimDialog,
}: ClaimDialogsProps) {
  return (
    <>
      <ClaimConfirmationDialog
        confirmText="Mark Completed"
        isLoading={isLoading}
        isOpen={showCompleteDialog}
        message="Are you sure you want to mark this claim as completed? This action cannot be undone."
        title="Mark Claim as Completed"
        variant="success"
        onClose={() => onSetCompleteDialog(false)}
        onConfirm={onCompleteConfirm}
      />
      <ClaimConfirmationDialog
        confirmText="Unclaim"
        isLoading={isLoading}
        isOpen={showUnclaimDialog}
        message="Are you sure you want to unclaim this need? This will cancel the volunteer's claim and make the need available again."
        title="Unclaim Need"
        variant="danger"
        onClose={() => onSetUnclaimDialog(false)}
        onConfirm={onUnclaimConfirm}
      />
    </>
  );
}

function triggerCallback(
  cb: ((token: string) => Promise<void>) | undefined,
  token: string,
) {
  if (cb) void cb(token);
}

function useClaimActionHandlers(
  claimToken: string,
  onMarkCompleted: ClaimActionsProps["onMarkCompleted"],
  onUnclaim: ClaimActionsProps["onUnclaim"],
  setShowCompleteDialog: (v: boolean) => void,
  setShowUnclaimDialog: (v: boolean) => void,
) {
  const handleUnclaimConfirm = () => {
    setShowUnclaimDialog(false);
    triggerCallback(onUnclaim, claimToken);
  };
  const handleCompleteConfirm = () => {
    setShowCompleteDialog(false);
    triggerCallback(onMarkCompleted, claimToken);
  };

  return { handleCompleteConfirm, handleUnclaimConfirm };
}

interface ClaimButtonsProps {
  isLoading: boolean;
  onMarkCompleted?: ClaimActionsProps["onMarkCompleted"];
  onUnclaim?: ClaimActionsProps["onUnclaim"];
  size: "sm" | "md";
  variant: "flat" | "solid";
  onShowComplete: () => void;
  onShowUnclaim: () => void;
}

function ClaimButtons({
  isLoading,
  onMarkCompleted,
  onShowComplete,
  onShowUnclaim,
  onUnclaim,
  size,
  variant,
}: ClaimButtonsProps) {
  return (
    <div className="flex gap-2">
      {onMarkCompleted && (
        <Button
          color="success"
          data-testid="mark-completed-button"
          isLoading={isLoading}
          size={size}
          startContent={<CheckCircle2 className="h-4 w-4" />}
          variant={variant}
          onPress={onShowComplete}
        >
          Mark Completed
        </Button>
      )}
      {onUnclaim && (
        <Button
          color="danger"
          data-testid="unclaim-button"
          isLoading={isLoading}
          size={size}
          startContent={<X className="h-4 w-4" />}
          variant={variant}
          onPress={onShowUnclaim}
        >
          Unclaim
        </Button>
      )}
    </div>
  );
}

export function ClaimActions({
  claimToken,
  isLoading = false,
  onMarkCompleted,
  onUnclaim,
  size = "sm",
  status,
  variant = "flat",
}: ClaimActionsProps) {
  const [showUnclaimDialog, setShowUnclaimDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const { handleCompleteConfirm, handleUnclaimConfirm } =
    useClaimActionHandlers(
      claimToken,
      onMarkCompleted,
      onUnclaim,
      setShowCompleteDialog,
      setShowUnclaimDialog,
    );

  if (status !== "claimed") return null;

  return (
    <>
      <ClaimButtons
        isLoading={isLoading}
        size={size}
        variant={variant}
        onMarkCompleted={onMarkCompleted}
        onShowComplete={() => setShowCompleteDialog(true)}
        onShowUnclaim={() => setShowUnclaimDialog(true)}
        onUnclaim={onUnclaim}
      />
      <ClaimDialogs
        claimToken={claimToken}
        isLoading={isLoading}
        showCompleteDialog={showCompleteDialog}
        showUnclaimDialog={showUnclaimDialog}
        onCompleteConfirm={handleCompleteConfirm}
        onSetCompleteDialog={setShowCompleteDialog}
        onSetUnclaimDialog={setShowUnclaimDialog}
        onUnclaimConfirm={handleUnclaimConfirm}
      />
    </>
  );
}
